import { useState, type CSSProperties } from 'react';
import { FLASH_MS, personaOf, type CommentaryPost } from '@edh/shared';
import { Sheet } from './Sheet';
import { initialsColor } from '../lib/avatar';
import type { CommentaryFeed } from '../lib/commentary';

/**
 * The peanut gallery.
 *
 * A parody timeline of accounts with money on the game. It is the one part of
 * the app that is purely for fun — nothing here is a measurement, and the
 * screen should read as a joke rather than as a readout, which is why it is the
 * only place using a proportional voice and round avatars rather than the
 * mono-numeral treatment everything else gets.
 *
 * Posts arrive on a schedule the server decided, so every phone at the table
 * sees the same post land at the same moment. The typing row is real in the
 * same sense: it is either a batch actually being written by the model, or a
 * post that is genuinely a few seconds from landing.
 */

const clock = (at: number, now: number): string => {
  const secs = Math.max(0, Math.round((now - at) / 1000));
  if (secs < 60) return `${secs}s`;
  const mins = Math.round(secs / 60);
  return mins < 60 ? `${mins}m` : `${Math.round(mins / 60)}h`;
};

const count = (n: number): string => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : `${n}`);

function Row({ post, now }: { post: CommentaryPost; now: number }) {
  return (
    <article className="tweet">
      <span
        className="tweet-face"
        style={{ background: initialsColor(post.name) }}
        aria-hidden="true"
      >
        {post.name.slice(0, 1).toUpperCase()}
      </span>
      <div className="tweet-body">
        <div className="tweet-head">
          <span className="tweet-name">{post.name}</span>
          <span className="tweet-handle">@{post.handle}</span>
          <span className="tweet-dot">·</span>
          <span className="tweet-age num">{clock(post.at, now)}</span>
        </div>
        <p className="tweet-text">{post.text}</p>
        <div className="tweet-meta num">
          <span>♻ {count(post.reposts)}</span>
          <span>♥ {count(post.likes)}</span>
        </div>
      </div>
    </article>
  );
}

function Typing({ handles }: { handles: string[] }) {
  if (handles.length === 0) return null;
  const names = handles.map((h) => personaOf(h)?.name ?? `@${h}`);
  const who =
    names.length === 1
      ? names[0]
      : names.length === 2
        ? `${names[0]} and ${names[1]}`
        : `${names[0]} and ${names.length - 1} others`;
  return (
    <div className="tweet-typing">
      <span className="tweet-typing-dots" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      <span>{who} typing…</span>
    </div>
  );
}

/**
 * The timeline, newest at the top.
 *
 * Which is the wrong way round for a conversation and the right way round for
 * this: it is a parody of a feed, everybody already knows how a feed is read,
 * and a table glancing at it mid-game wants the latest thing rather than the
 * beginning. The composing row sits above everything for the same reason -
 * that is where the next post is going to appear.
 */
function Timeline({ feed, now }: { feed: CommentaryFeed; now: number }) {
  return (
    <div className="timeline">
      <Typing handles={feed.typing} />
      {[...feed.posts].reverse().map((post) => (
        <Row key={post.id} post={post} now={now} />
      ))}
    </div>
  );
}

/**
 * A post's moment on the table.
 *
 * The feed proper is a sheet, and a sheet is the wrong shape for a game in
 * progress — nobody puts the counter down mid-combat to read a timeline. So a
 * post gets held over the table as it lands and then fades out of the way. Tap
 * it to open the rest.
 *
 * Keyed on the post id so that a new one restarts the animation rather than
 * inheriting the tail end of the last one's fade.
 */
export function CommentaryFlash({
  feed,
  onOpen,
}: {
  feed: CommentaryFeed;
  onOpen: () => void;
}) {
  /*
   * Posts this phone has waved away.
   *
   * Per phone rather than per table: dismissing a post is somebody deciding
   * they have read it, which is not a fact about the game and has no business
   * on the shared document. Ids rather than a boolean, so waving one away does
   * not also swallow the next one.
   */
  const [dismissed, setDismissed] = useState<string[]>([]);
  const showing = feed.flash && !dismissed.includes(feed.flash.id) ? feed.flash : null;
  /*
   * Posts only. No typing row here.
   *
   * Over the table it was announcing that a joke was coming and then sitting
   * there, which is a notification about a notification - and the schedule
   * means the wait can be a minute or more. Composing belongs in the feed,
   * where somebody has chosen to watch it happen.
   */
  if (!showing) return null;

  return (
    <div
      className="ticker ticker-flash"
      key={showing.id}
      /* The fade and the countdown ring both run for exactly as long as the
         post is going to be shown, and that length lives in one place. Handing
         it to CSS as a variable is what stops the animation and the clock that
         removes the post from drifting apart. */
      style={{ '--flash': `${FLASH_MS}ms` } as CSSProperties}
    >
      <button className="ticker-open" onClick={onOpen} aria-label="open the feed">
        <span
          className="ticker-face"
          style={{ background: initialsColor(showing.name) }}
          aria-hidden="true"
        >
          {showing.name.slice(0, 1).toUpperCase()}
        </span>
        <span className="ticker-text">
          <span className="ticker-handle">@{showing.handle}</span> {showing.text}
        </span>
      </button>

      {/*
        The ring is the clock: it empties over exactly as long as the post has
        left, so the thing telling you it is about to go is the same thing you
        press to make it go now. A post that vanishes on its own timer with no
        warning reads as a glitch.
      */}
      <button
        className="ticker-close"
        onClick={() => setDismissed((d) => [...d, showing.id])}
        aria-label="dismiss this post"
      >
        <svg className="ticker-ring" viewBox="0 0 24 24" aria-hidden="true">
          <circle className="ticker-ring-track" cx="12" cy="12" r="10" />
          <circle className="ticker-ring-run" cx="12" cy="12" r="10" />
        </svg>
        <span className="ticker-x" aria-hidden="true">
          ×
        </span>
      </button>
    </div>
  );
}

/**
 * The timeline inline, for a screen with room for it.
 *
 * The lobby is where this earns its place: the odds are on screen, nobody has
 * drawn a card yet, and there is nothing else to look at. A sheet somebody has
 * to know to open would be the wrong answer to "why is it quiet".
 */
export function CommentaryPanel({ feed }: { feed: CommentaryFeed }) {
  const now = Date.now();
  if (feed.posts.length === 0 && feed.typing.length === 0) return null;

  return (
    <>
      <div className="section-head">
        <h2>Twitter feed</h2>
      </div>
      <Timeline feed={feed} now={now} />
    </>
  );
}

/**
 * The feed a game left behind.
 *
 * Takes posts rather than a live feed, because by the time anybody reads this
 * the game is over: it is either the closing burst arriving while the result
 * screen is still up, or the whole conversation read back off the match months
 * later. Every post has landed, so there is no schedule to respect and nothing
 * to wait for.
 */
export function CommentaryRecord({ posts }: { posts: CommentaryPost[] }) {
  if (posts.length === 0) return null;
  const now = Date.now();
  return (
    <>
      <div className="section-head">
        <h2>Twitter feed</h2>
      </div>
      <div className="timeline">
        {[...posts]
          .sort((a, b) => b.at - a.at)
          .map((post) => (
            <Row key={post.id} post={post} now={now} />
          ))}
      </div>
    </>
  );
}

/** The whole timeline, newest last so it reads like a room talking. */
export function CommentaryFeedSheet({
  feed,
  onClose,
}: {
  feed: CommentaryFeed;
  onClose: () => void;
}) {
  const now = Date.now();
  return (
    <Sheet title="Twitter feed" onClose={onClose}>
      {feed.posts.length === 0 && feed.typing.length === 0 ? (
        <div className="empty">Nothing yet. Give them a minute.</div>
      ) : (
        <Timeline feed={feed} now={now} />
      )}
    </Sheet>
  );
}
